/**
 * @file AuctionPreviewSheet.tsx
 * @description 경매 물건 미리보기 시트 컴포넌트
 *
 * 이 컴포넌트는 경매 물건의 객관적 사실(Fact)과 가격 정보만을 보여주는
 * 미리보기 시트를 구현합니다. 시스템이 권리 관계나 안전 여부를 판단하는
 * 문구는 절대 포함하지 않습니다.
 *
 * 주요 기능:
 * 1. Header: 사건번호 Badge, 물건 이름, 주소 표시 (주소 클릭 시 네이버 지도로 이동)
 * 2. Map Section: 지도 섹션 (클릭 시 네이버 지도로 이동)
 * 3. Price Potential: 감정가 vs 최저가 비교 표시
 * 4. Object Specs: 물건 기본 정보 2x2 그리드 표시
 * 5. Footer: 수익 시뮬레이션 하러 가기 CTA
 *
 * @dependencies
 * - @/lib/types/real-auction: RealAuction 타입
 * - @/components/shared/ui/sheet: Sheet 컴포넌트
 * - @/components/shared/ui/badge: Badge 컴포넌트
 * - @/components/shared/ui/skeleton: Skeleton 컴포넌트
 * - next/navigation: useRouter 훅 (리다이렉트)
 * - lucide-react: MapPin, TrendingUp, Calculator, ExternalLink 아이콘
 *
 * @see {@link /docs/product/current-architecture-ssot.md} - 현재 사건 탐색 및 미리보기 역할
 */

'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { MapPin, TrendingUp, Calculator, ExternalLink, Bookmark } from 'lucide-react';

import { RealAuction } from '@/lib/core/types/real-auction';
import { Badge } from '@/components/shared/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/shared/ui/sheet';
import { Skeleton } from '@/components/shared/ui/skeleton';
import { Button } from '@/components/shared/ui/button';

interface AuctionPreviewSheetProps {
  /** 미리보기할 경매 물건 데이터 */
  auction: RealAuction | null;
  /** 시트 열림 상태 */
  open: boolean;
  /** 시트 닫기 핸들러 */
  onOpenChange: (open: boolean) => void;
  /** CTA 버튼 클릭 핸들러 (선택적, 제공되지 않으면 내부에서 처리) */
  onSimulateClick?: () => void;
}

/**
 * 경매 물건 미리보기 시트 컴포넌트
 *
 * 객관적 사실과 가격 정보만을 중립적으로 표시하며,
 * 시스템의 판단이나 권장사항은 포함하지 않습니다.
 */
export function AuctionPreviewSheet({
  auction,
  open,
  onOpenChange,
  onSimulateClick
}: AuctionPreviewSheetProps) {
  const router = useRouter();
  const [isFavorite, setIsFavorite] = useState(false);
  const [isCheckingFavorite, setIsCheckingFavorite] = useState(false);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);

  // 관심물건 상태 확인
  useEffect(() => {
    if (!auction || !open) return;

    const checkFavoriteStatus = async () => {
      setIsCheckingFavorite(true);
      try {
        const response = await fetch('/api/user/favorites');
        if (response.ok) {
          const data = await response.json();
          const favorite = data.favorites?.find(
            (fav: any) =>
              fav.caseNo === auction.caseNo &&
              fav.disposalSeq === auction.disposalSeq
          );
          setIsFavorite(!!favorite);
        }
      } catch (error) {
        console.error('관심물건 상태 확인 실패:', error);
      } finally {
        setIsCheckingFavorite(false);
      }
    };

    checkFavoriteStatus();
  }, [auction, open]);

  // 관심물건 토글 핸들러
  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!auction || isTogglingFavorite) return;

    setIsTogglingFavorite(true);
    try {
      if (isFavorite) {
        // 삭제
        const response = await fetch(
          `/api/user/favorites?caseNo=${encodeURIComponent(auction.caseNo)}&disposalSeq=${auction.disposalSeq}`,
          { method: 'DELETE' }
        );
        if (response.ok) {
          setIsFavorite(false);
        }
      } else {
        // 추가
        const response = await fetch('/api/user/favorites', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            caseNo: auction.caseNo,
            disposalSeq: auction.disposalSeq,
          }),
        });
        if (response.ok) {
          setIsFavorite(true);
        }
      }
    } catch (error) {
      console.error('관심물건 토글 실패:', error);
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  if (!auction) return null;

  /**
   * 수익 시뮬레이션 버튼 클릭 핸들러
   * 커스텀 핸들러가 제공된 경우 실행, 없으면 분석 페이지로 이동
   */
  const handleSimulateClick = () => {
    // 커스텀 핸들러가 제공된 경우 실행
    if (onSimulateClick) {
      onSimulateClick();
      return;
    }

    // 기본 동작: 분석 페이지로 이동
    const encodedCaseNo = encodeURIComponent(auction.caseNo);
    const encodedDisposalSeq = encodeURIComponent(auction.disposalSeq);
    router.push(`/analysis/${encodedCaseNo}/${encodedDisposalSeq}`);
  };

  /**
   * 주소를 클릭했을 때 네이버 지도로 이동하는 핸들러
   */
  const handleAddressClick = () => {
    if (!auction.address) return;
    const encodedAddress = encodeURIComponent(auction.address);
    window.open(`https://map.naver.com/v5/search/${encodedAddress}`, '_blank');
  };

  /**
   * 지도 섹션을 클릭했을 때 네이버 지도로 이동하는 핸들러
   */
  const handleMapClick = () => {
    if (!auction.address) return;
    const encodedAddress = encodeURIComponent(auction.address);
    window.open(`https://map.naver.com/v5/search/${encodedAddress}`, '_blank');
  };

  // 가격 차이 계산 (감정가 대비 최저가 차이)
  const priceGap = auction.minimumBidPrice - auction.appraisedValue;
  const priceGapText = priceGap > 0 ? `${formatPrice(priceGap)} 차이` : `${formatPrice(Math.abs(priceGap))} 낮음`;

  // 층수 정보 추출 (buildingDetail에서)
  const floorInfo = extractFloorInfo(auction.buildingDetail);

  // 매각기일 포맷팅
  const auctionDecisionDate = auction.auctionDecisionDate
    ? new Date(auction.auctionDecisionDate).toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    : '정보 없음';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full flex flex-col px-4 sm:px-8" style={{ maxWidth: '576px' }}>
        {/* Header */}
        <SheetHeader className="flex-shrink-0 border-b border-zinc-100 dark:border-zinc-700 pb-6">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <Badge variant="secondary" className="text-[11px] font-bold px-2 py-1 mb-2 tracking-wide">
                {auction.caseNo}
              </Badge>
              <SheetTitle className="text-xl font-black text-zinc-900 dark:text-zinc-100 leading-tight">
                {auction.buildingName || '건물명 정보 없음'}
                <br />
                {auction.buildingDetail || ''}
              </SheetTitle>
              <p 
                className={`text-sm text-zinc-500 dark:text-zinc-400 mt-1 ${auction.address ? 'cursor-pointer hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors flex items-center gap-1 group' : ''}`}
                onClick={handleAddressClick}
              >
                {auction.address || '주소 정보 없음'}
                {auction.address && (
                  <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                )}
              </p>
            </div>
            {/* 관심물건 찜 버튼 */}
            <Button
              variant={isFavorite ? "default" : "outline"}
              size="sm"
              onClick={handleToggleFavorite}
              disabled={isCheckingFavorite || isTogglingFavorite}
              className={`ml-4 flex-shrink-0 gap-1.5 ${
                isFavorite
                  ? 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white border-blue-600 dark:border-blue-500'
                  : 'border-zinc-300 dark:border-zinc-600 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'
              }`}
              aria-label={isFavorite ? '관심물건 해제' : '관심물건 추가'}
            >
              <Bookmark
                className={`w-4 h-4 transition-all ${
                  isFavorite ? 'fill-current' : ''
                }`}
              />
              <span className="text-xs font-medium">
                {isFavorite ? '찜함' : '찜하기'}
              </span>
            </Button>
          </div>
        </SheetHeader>

        {/* Body */}
        <div className="flex-1 overflow-y-auto py-6 space-y-8">
          {/* Map Section */}
          <div>
            <div 
              className={`w-full h-40 bg-zinc-100 dark:bg-zinc-800 rounded-xl flex items-center justify-center relative overflow-hidden group ${auction.address ? 'cursor-pointer hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors' : ''}`}
              onClick={handleMapClick}
            >
              <Skeleton className="w-full h-full absolute inset-0" />
              <div className="text-center text-zinc-400 dark:text-zinc-500 z-10">
                <MapPin className={`w-6 h-6 mx-auto mb-1 ${auction.address ? 'group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors' : ''}`} />
                <span className="text-xs font-bold">
                  {auction.address ? '지도에서 위치 확인하기' : '주소 정보 없음'}
                </span>
                {auction.address && (
                  <ExternalLink className="w-3 h-3 mx-auto mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                )}
              </div>
            </div>
          </div>

          {/* Price Potential */}
          <div className="bg-emerald-50/50 dark:bg-emerald-950/30 border border-emerald-100 dark:border-emerald-800/50 rounded-xl p-4 sm:p-5">
            <div className="flex justify-between items-center mb-4">
              <span className="text-xs font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider flex items-center gap-1">
                <TrendingUp className="w-3 h-3" />
                Price Potential
              </span>
            </div>

            <div className="flex justify-between items-end mb-2">
              <div className="text-left">
                <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-1">현재 최저가</p>
                <p className="text-2xl font-black text-zinc-900 dark:text-zinc-100 font-mono">
                  {formatPrice(auction.minimumBidPrice)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-zinc-400 dark:text-zinc-500 mb-1">감정가 대비</p>
                <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                  {priceGapText}
                </p>
              </div>
            </div>

            <div className="w-full h-2 bg-zinc-200 dark:bg-zinc-700 rounded-full overflow-hidden flex">
              <div
                className="h-full bg-zinc-400 dark:bg-zinc-500"
                style={{
                  width: `${Math.min((auction.appraisedValue / auction.minimumBidPrice) * 100, 100)}%`
                }}
              />
              <div
                className="h-full bg-emerald-400 dark:bg-emerald-500"
                style={{
                  width: `${Math.max(0, ((auction.minimumBidPrice - auction.appraisedValue) / auction.minimumBidPrice) * 100)}%`
                }}
              />
            </div>
            <p className="text-[10px] text-zinc-400 dark:text-zinc-500 mt-2 text-right">
              * 권리 분석 및 추가 비용에 따라 실제 수익은 달라집니다.
            </p>
          </div>

          {/* Object Specs */}
          <div>
            <h3 className="text-sm font-bold text-zinc-900 dark:text-zinc-100 uppercase mb-3">물건 기본 정보</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-zinc-50 dark:bg-zinc-800 rounded-lg border border-zinc-100 dark:border-zinc-700">
                <p className="text-xs text-zinc-400 dark:text-zinc-500">건물 면적</p>
                <p className="text-base font-bold text-zinc-700 dark:text-zinc-300">
                  {auction.buildingArea ? `${auction.buildingArea.toFixed(2)}㎡ (${Math.round(auction.buildingArea * 0.3025)}평)` : '정보 없음'}
                </p>
              </div>
              <div className="p-3 bg-zinc-50 dark:bg-zinc-800 rounded-lg border border-zinc-100 dark:border-zinc-700">
                <p className="text-xs text-zinc-400 dark:text-zinc-500">유찰횟수</p>
                <p className="text-base font-bold text-zinc-700 dark:text-zinc-300">
                  {auction.failureCount === 0 ? '신건' : `${auction.failureCount}회`}
                </p>
              </div>
              <div className="p-3 bg-zinc-50 dark:bg-zinc-800 rounded-lg border border-zinc-100 dark:border-zinc-700">
                <p className="text-xs text-zinc-400 dark:text-zinc-500">층수</p>
                <p className="text-base font-bold text-zinc-700 dark:text-zinc-300">{floorInfo}</p>
              </div>
              <div className="p-3 bg-zinc-50 dark:bg-zinc-800 rounded-lg border border-zinc-100 dark:border-zinc-700">
                <p className="text-xs text-zinc-400 dark:text-zinc-500">매각기일</p>
                <p className="text-base font-bold text-zinc-700 dark:text-zinc-300">{auctionDecisionDate}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-zinc-100 dark:border-zinc-700 pt-6">
          <p className="text-xs text-center text-zinc-400 dark:text-zinc-500 mb-3">
            이 물건의 숨은 비용을 직접 확인하고 수익을 계산하세요.
          </p>
          <button
            onClick={handleSimulateClick}
            className="w-full py-4 bg-zinc-900 dark:bg-zinc-800 hover:bg-black dark:hover:bg-zinc-700 text-white rounded-xl font-bold text-lg shadow-lg transition flex items-center justify-center gap-2 group"
          >
            <Calculator className="w-5 h-5 group-hover:text-emerald-400 transition-colors" />
            물건 정보 보러 가기
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * 가격을 억 단위로 포맷팅하는 유틸리티 함수
 */
function formatPrice(price: number): string {
  const billion = price / 100000000; // 억 단위로 변환
  return `${billion.toFixed(1)}억`;
}

/**
 * 건물 상세 정보에서 층수 정보를 추출하는 유틸리티 함수
 */
function extractFloorInfo(buildingDetail: string | null): string {
  if (!buildingDetail) return '정보 없음';

  // 정규식을 사용하여 층수 패턴 찾기 (예: "12층", "12층/25층" 등)
  const floorMatch = buildingDetail.match(/(\d+)\s*층(?:\s*\/\s*(\d+)\s*층)?/);
  if (floorMatch) {
    const currentFloor = floorMatch[1];
    const totalFloor = floorMatch[2];
    return totalFloor ? `${currentFloor}층 / ${totalFloor}층` : `${currentFloor}층`;
  }

  return '정보 없음';
}
